import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useAuth } from "./useAuth";
import { useOrderOnBehalf } from "./useOrderOnBehalf";

interface CartContextType {
  cartCount: number;
  refreshCart: () => Promise<void>;
  addToCart: (productId: number, quantity?: number) => Promise<{ success: boolean; warning?: string; error?: string }>;
  clearCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType>({
  cartCount: 0,
  refreshCart: async () => {},
  addToCart: async () => ({ success: false }),
  clearCart: async () => {},
});

export function CartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { customer: onBehalfCustomer } = useOrderOnBehalf();
  const [cartCount, setCartCount] = useState(0);

  const forCustomerId = onBehalfCustomer?.id ?? null;

  const buildUrl = useCallback((base: string) => {
    if (forCustomerId) {
      const sep = base.includes("?") ? "&" : "?";
      return `${base}${sep}forCustomerId=${forCustomerId}`;
    }
    return base;
  }, [forCustomerId]);

  const refreshCart = useCallback(async () => {
    if (!user) {
      setCartCount(0);
      return;
    }
    try {
      const res = await fetch(buildUrl("/api/cart"), { credentials: "include" });
      if (!res.ok) {
        setCartCount(0);
        return;
      }
      const data = await res.json();
      const items = Array.isArray(data) ? data : [];
      setCartCount(items.reduce((sum: number, item: any) => sum + item.quantity, 0));
    } catch {
      setCartCount(0);
    }
  }, [user, buildUrl]);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const addToCart = useCallback(async (productId: number, quantity: number = 1): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch(buildUrl("/api/cart"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ productId, quantity, forCustomerId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { success: false, error: data.error || "Failed to add to cart" };
      }
      const data = await res.json().catch(() => ({}));
      await refreshCart();
      return { success: true, warning: data.warning };
    } catch {
      return { success: false, error: "Failed to add to cart" };
    }
  }, [refreshCart, buildUrl, forCustomerId]);

  const clearCart = useCallback(async () => {
    try {
      const res = await fetch(buildUrl("/api/cart"), { method: "DELETE", credentials: "include" });
      if (res.ok) {
        setCartCount(0);
      }
    } catch {}
  }, [buildUrl]);

  return (
    <CartContext.Provider value={{ cartCount, refreshCart, addToCart, clearCart }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
